import { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, Moon, Star, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { getIslamicEvents, IslamicEvent, getRamadanInfo, RamadanInfo } from '../utils/islamicEvents';
import { getHijriDate, getHijriYear } from '../utils/hijri';
import { PrayerDisplay } from '../types/prayer';

interface IslamicEventsPanelProps {
  prayers: PrayerDisplay[];
}

export function IslamicEventsPanel({ prayers }: IslamicEventsPanelProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [events, setEvents] = useState<IslamicEvent[]>([]);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [nextPrayer, setNextPrayer] = useState<{ name: string; time: string; timeLeft: string } | null>(null);
  const [ramadanInfo, setRamadanInfo] = useState<RamadanInfo | null>(null);
  const [currentSection, setCurrentSection] = useState(0);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const parseTimeToMinutes = (timeStr: string, prayerKey?: string): number => {
    if (!timeStr) return 0;

    const [hoursStr, minutesStr] = timeStr.split(':');
    let hours = parseInt(hoursStr);
    const minutes = parseInt(minutesStr);

    if (prayerKey) {
      const key = prayerKey.toLowerCase();
      if (key === 'fajr' || key === 'sunrise') {
        if (hours === 12) hours = 0;
      } else if (key === 'zuhr' || key === 'dhuhr') {
        if (hours !== 12) hours += 12;
      } else if (key === 'asr' || key === 'maghrib' || key === 'isha') {
        if (hours !== 12) hours += 12;
      }
    }

    return hours * 60 + minutes;
  };

  useEffect(() => {
    if (prayers.length === 0) return;

    const updateNextPrayer = () => {
      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();

      let nextPrayerInfo = null;
      let minDiff = Infinity;

      for (const prayer of prayers) {
        if (prayer.name === 'Sunrise') continue;
        const prayerMinutes = parseTimeToMinutes(prayer.time, prayer.key);
        const diff = prayerMinutes - currentMinutes;

        if (diff > 0 && diff < minDiff) {
          minDiff = diff;
          const hoursLeft = Math.floor(diff / 60);
          const minutesLeft = diff % 60;

          let timeLeftStr = '';
          if (hoursLeft > 0) {
            timeLeftStr = `${hoursLeft}h ${minutesLeft}m`;
          } else {
            timeLeftStr = `${minutesLeft}m`;
          }

          nextPrayerInfo = {
            name: prayer.name,
            time: prayer.time,
            timeLeft: timeLeftStr
          };
        }
      }

      if (!nextPrayerInfo) {
        for (const prayer of prayers) {
          if (prayer.name === 'Sunrise') continue;
          const prayerMinutes = parseTimeToMinutes(prayer.time, prayer.key);
          const diff = prayerMinutes - currentMinutes + 24 * 60;

          if (diff < minDiff) {
            minDiff = diff;
            const hoursLeft = Math.floor(diff / 60);
            const minutesLeft = diff % 60;

            let timeLeftStr = '';
            if (hoursLeft > 0) {
              timeLeftStr = `${hoursLeft}h ${minutesLeft}m`;
            } else {
              timeLeftStr = `${minutesLeft}m`;
            }

            nextPrayerInfo = {
              name: prayer.name,
              time: prayer.time,
              timeLeft: timeLeftStr
            };
          }
        }
      }

      setNextPrayer(nextPrayerInfo);
    };

    updateNextPrayer();
    const interval = setInterval(updateNextPrayer, 1000);

    return () => clearInterval(interval);
  }, [prayers]);

  useEffect(() => {
    const fetchEvents = async () => {
      const islamicEvents = await getIslamicEvents();

      if (showAllEvents) {
        setEvents(islamicEvents);
      } else {
        const thirtyDaysAgo = new Date(currentTime.getTime() - 30 * 24 * 60 * 60 * 1000);
        const oneYearFromNow = new Date(currentTime.getTime() + 365 * 24 * 60 * 60 * 1000);

        const relevantEvents = islamicEvents.filter(e => {
          return e.date >= thirtyDaysAgo && e.date <= oneYearFromNow;
        });

        setEvents(relevantEvents);
      }

      const ramadan = await getRamadanInfo();
      setRamadanInfo(ramadan);
    };

    fetchEvents();
  }, [currentTime, showAllEvents]);

  const getTimeUntil = (eventDate: Date) => {
    const diff = eventDate.getTime() - currentTime.getTime();
    const absDiff = Math.abs(diff);
    const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((absDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));

    const isPast = diff < 0;
    const suffix = isPast ? 'ago' : 'remaining';

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m ${suffix}`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${suffix}`;
    } else {
      return `${minutes}m ${suffix}`;
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'eid': return <Star className="w-6 h-6" />;
      case 'ramadan': return <Moon className="w-6 h-6" />;
      case 'hajj': return <Calendar className="w-6 h-6" />;
      default: return <Calendar className="w-6 h-6" />;
    }
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'eid': return 'from-amber-500 to-orange-500';
      case 'ramadan': return 'from-purple-500 to-indigo-500';
      case 'hajj': return 'from-green-500 to-teal-500';
      default: return 'from-blue-500 to-cyan-500';
    }
  };

  const upcomingEvents = events.filter(e => e.date > currentTime);
  const nextEvent = ramadanInfo?.isRamadan ? null : upcomingEvents[0];

  // Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const swipeThreshold = 50;
    const diff = touchStartX.current - touchEndX.current;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        navigateNext();
      } else {
        navigatePrevious();
      }
    }
  };

  const navigateNext = () => {
    setCurrentSection((prev) => Math.min(prev + 1, 2));
  };

  const navigatePrevious = () => {
    setCurrentSection((prev) => Math.max(prev - 1, 0));
  };

  // Reference dates from Sara International Travel website
  const referenceDates = [
    { name: "Lailat al-Bara'ah", date: "February 3", hijri: "15th Shaban 1447" },
    { name: "Ramadan 2026", date: "February 18 to March 19", hijri: "1st Ramadan 1447" },
    { name: "Eid al-Fitr 2026", date: "March 20", hijri: "1st Shawwal 1447" },
    { name: "Hajj Begins", date: "May 22", hijri: "8th Dhul Hijjah 1447" },
    { name: "Day of Arafah", date: "May 26", hijri: "9th Dhul Hijjah 1447" },
    { name: "Eid al-Adha", date: "May 27", hijri: "10th Dhul Hijjah 1447" },
    { name: "Islamic New Year 1448", date: "June 17", hijri: "1st Muharram 1448" },
    { name: "Ashura", date: "June 26", hijri: "10th Muharram 1448" },
    { name: "Eid Milad un Nabi", date: "August 25", hijri: "12th Rabi Al Awwal 1448" }
  ];

  return (
    <div
      className="mb-4 sm:mb-6 space-y-3 sm:space-y-4 relative"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Navigation Arrows (visible on mobile/tablet) */}
      <div className="flex justify-between items-center mb-2 md:hidden">
        <button
          onClick={navigatePrevious}
          disabled={currentSection === 0}
          className={`p-2 rounded-full ${currentSection === 0 ? 'bg-white/10 text-white/30' : 'bg-white/20 text-white hover:bg-white/30'} transition-all`}
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <div className="flex gap-2">
          {[0, 1, 2].map((index) => (
            <button
              key={index}
              onClick={() => setCurrentSection(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                currentSection === index ? 'bg-white w-8' : 'bg-white/30'
              }`}
            />
          ))}
        </div>
        <button
          onClick={navigateNext}
          disabled={currentSection === 2}
          className={`p-2 rounded-full ${currentSection === 2 ? 'bg-white/10 text-white/30' : 'bg-white/20 text-white hover:bg-white/30'} transition-all`}
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Swipe Hint Text */}
      <div className="text-center text-white/60 text-sm mb-2 md:hidden">
        ← Swipe to navigate →
      </div>

      {/* Section 0: Time and Next Prayer */}
      <div className={`transition-all duration-300 ${currentSection === 0 ? 'block' : 'hidden md:block'}`}>
        <div className="bg-gradient-to-br from-teal-700/30 to-emerald-700/30 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-2xl border-2 border-teal-400/50 px-2 py-2 sm:px-3 sm:py-3 md:px-4 md:py-4">
          <div className="flex flex-col items-center gap-1 sm:gap-2">
            <div className="text-center w-full">
              <div
                className="font-black text-white drop-shadow-2xl tracking-wider leading-none"
                style={{ fontSize: 'clamp(2.5rem, 12vw, 8rem)' }}
              >
                {currentTime.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true
                }).replace(/\s?(AM|PM|am|pm)/g, '')}
              </div>
              <div className="text-xs sm:text-sm md:text-base lg:text-lg text-white/90 mt-0.5 sm:mt-1 font-semibold">
                {currentTime.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>

            {nextPrayer && (
              <div className="mt-0.5 sm:mt-1 pt-1 sm:pt-2 border-t-2 border-white/20 w-full">
                <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
                  <Moon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 flex-shrink-0 text-white" />
                  <div
                    className="flex items-baseline gap-1 sm:gap-2 md:gap-3 flex-wrap justify-center font-bold text-white"
                    style={{ fontSize: 'clamp(0.875rem, 3vw, 2.5rem)' }}
                  >
                    <span>{nextPrayer.name}</span>
                    <span className="font-semibold">at {nextPrayer.time}</span>
                    <span
                      className="bg-white/20 rounded-lg px-2 py-0.5 sm:px-3 sm:py-1"
                      style={{ fontSize: 'clamp(0.75rem, 2.5vw, 2rem)' }}
                    >
                      in {nextPrayer.timeLeft}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {ramadanInfo?.isRamadan ? (
          <div className="bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white mt-3 sm:mt-4">
            <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
              <div className="flex-1 w-full">
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <Moon className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 flex-shrink-0" />
                  <div className="min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold">Ramadan Mubarak</h2>
                    <p className="text-2xl sm:text-3xl font-arabic opacity-90">رمضان مبارك</p>
                  </div>
                </div>
                <p className="text-white/90 mb-3 sm:mb-4 text-sm sm:text-base">The blessed month of fasting</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs sm:text-sm">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>Day {ramadanInfo.dayOfRamadan} of 30</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Star className="w-4 h-4" />
                    <span>{ramadanInfo.daysRemaining} days remaining</span>
                  </div>
                </div>
                <div className="mt-4 bg-white/20 backdrop-blur-sm rounded-xl p-4">
                  <p className="text-sm font-semibold mb-2">Daily Reminder</p>
                  <p className="text-sm">Increase your worship, recite Quran, give charity, and make sincere dua. May Allah accept your fasting and prayers.</p>
                </div>
              </div>
              <div className="text-right">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                  <Moon className="w-6 h-6 mx-auto mb-2" />
                  <div className="text-5xl font-bold">{ramadanInfo.dayOfRamadan}</div>
                  <div className="text-sm opacity-90">of 30</div>
                </div>
              </div>
            </div>
          </div>
        ) : nextEvent && (
          <div className={`bg-gradient-to-r ${getEventColor(nextEvent.type)} rounded-2xl p-6 text-white mt-3 sm:mt-4`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  {getEventIcon(nextEvent.type)}
                  <div>
                    <h2 className="text-2xl font-bold">{nextEvent.name}</h2>
                    <p className="text-3xl font-arabic opacity-90">{nextEvent.nameArabic}</p>
                  </div>
                </div>
                <p className="text-white/90 mb-4">{nextEvent.description}</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4" />
                    <span>{nextEvent.date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm pl-6 text-white/80">
                    <span className="font-arabic">{getHijriDate(nextEvent.date)}</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                  <Clock className="w-6 h-6 mx-auto mb-2" />
                  <div className="text-3xl font-bold">{getTimeUntil(nextEvent.date)}</div>
                  <div className="text-sm opacity-90">remaining</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section 1: Reference Dates Table */}
      <div className={`transition-all duration-300 ${currentSection === 1 ? 'block' : 'hidden md:block'}`}>
        <div className="bg-gradient-to-br from-blue-900/50 to-purple-900/50 backdrop-blur-md rounded-2xl p-6 border-2 border-blue-400/30">
          <div className="mb-4">
            <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" />
              Important Islamic Dates 2026 (Hijri 1447-1448)
            </h3>
            <p className="text-white/70 text-sm">
              Reference: Sara International Travel • Verified with Aladhan API
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-white">
              <thead>
                <tr className="border-b-2 border-white/20">
                  <th className="text-left py-3 px-4 font-bold text-sm">Important Day</th>
                  <th className="text-left py-3 px-4 font-bold text-sm">Date (2026)</th>
                  <th className="text-left py-3 px-4 font-bold text-sm">Islamic Hijri Date</th>
                </tr>
              </thead>
              <tbody>
                {referenceDates.map((ref, index) => (
                  <tr key={index} className="border-b border-white/10 hover:bg-white/5 transition-colors">
                    <td className="py-3 px-4 text-sm font-semibold">{ref.name}</td>
                    <td className="py-3 px-4 text-sm">{ref.date}</td>
                    <td className="py-3 px-4 text-sm font-arabic">{ref.hijri}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-xs text-white/60 italic">
            <p>Note: Actual dates may vary by 1-2 days depending on moon sighting. These dates are based on astronomical calculations.</p>
            <p className="mt-1">Source: <a href="https://sarainternationaltravel.com/islamic-calendar-2026/" target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:text-blue-200 underline">Sara International Travel Islamic Calendar 2026</a></p>
          </div>
        </div>
      </div>

      {/* Section 2: All Events */}
      <div className={`transition-all duration-300 ${currentSection === 2 ? 'block' : 'hidden md:block'}`}>
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                All Islamic Events {currentTime.getFullYear()} / {getHijriYear(currentTime)}
              </h3>
              <p className="text-white/70 text-sm">
                {events.length} events {showAllEvents ? '(all events)' : '(past 30 days and upcoming)'}
              </p>
            </div>
            <button
              onClick={() => setShowAllEvents(!showAllEvents)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all font-semibold ${
                showAllEvents
                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                  : 'bg-white/20 hover:bg-white/30 text-white'
              }`}
            >
              <Filter className="w-4 h-4" />
              {showAllEvents ? 'Show Filtered' : 'Show All Events'}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[600px] overflow-y-auto">
            {events.map((event, index) => {
              const isPast = event.date < currentTime;
              return (
                <div key={index} className={`rounded-xl p-4 transition-all ${isPast ? 'bg-white/5 opacity-60' : 'bg-white/10 hover:bg-white/20'}`}>
                  <div className="flex items-start gap-3 mb-2">
                    <div className="flex-shrink-0 mt-1">
                      {getEventIcon(event.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white mb-1">{event.name}</div>
                      <div className="text-base font-arabic text-white/90 mb-2">{event.nameArabic}</div>
                      <div className="text-xs text-white/70 mb-2">{event.description}</div>
                      <div className="space-y-1">
                        <div className="text-xs text-white/80">
                          {event.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                        <div className="text-xs text-white/70 font-arabic">
                          {getHijriDate(event.date)}
                        </div>
                      </div>
                      <div className={`mt-2 text-xs font-semibold ${isPast ? 'text-gray-400' : 'text-amber-300'}`}>
                        {getTimeUntil(event.date)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
